import { Box, Tag, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

export type PaymentDetail = {
  id: string
  cardAlias?: string | null
  date: string
  ownerName?: string | null
  merchantName?: string | null
  merchantAmountCents: number
  merchantCurrency: string
  amountCents: number
  currency: string
  exchangeRate: number
  type: string
}

interface ContentProps {
  paymentDetail: PaymentDetail
}

interface AmountWithCurrencyProps {
  amount: number
  currency: string
}

const AmountWithCurrency = ({ amount, currency }: AmountWithCurrencyProps): JSX.Element => {
  const value = Math.abs(amount)

  const {
    company: { locale },
  } = useCurrentSession()

  const amountWithCurrency = formattedAmount({
    amountCents: value,
    currency,
    locale: locale || '',
  })

  return (
    <Text weight="normal" size="300" color="grey800">
      {amountWithCurrency}
    </Text>
  )
}

const TransactionDetailContent = ({ paymentDetail }: ContentProps) => {
  const {
    type,
    cardAlias,
    ownerName,
    date,
    merchantName,
    merchantAmountCents,
    merchantCurrency,
    amountCents,
    currency,
    exchangeRate,
  } = paymentDetail

  return (
    <Box width="full" justifyContent="flexStart" alignItems="flexStart" gap="s32">
      <Box width="full" justifyContent="flexStart" alignItems="flexStart" gap="s16">
        <Text.Header2 color="grey1000">
          {i18n.t(
            'software_management.software_setups.software_payment_detail.invoice.titles.cost_information'
          )}
        </Text.Header2>
        <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
          <Box width="full" textAlign="left" gap="s8">
            <Text weight="medium" size="300" color="grey1000">
              {i18n.t('expenses_v2.transactions.detail.fields.type')}
            </Text>
            <Box height="s24">
              <Tag leftItem="circle" showCircle type="champagne" inline>
                {type}
              </Tag>
            </Box>
          </Box>
          {cardAlias && (
            <Box width="full" textAlign="left" gap="s8">
              <Text weight="medium" size="300" color="grey1000">
                {i18n.t('expenses_v2.transactions.detail.fields.card_alias')}
              </Text>
              <Box height="s24">
                <Tag type="grey" inline>
                  {cardAlias}
                </Tag>
              </Box>
            </Box>
          )}
        </Box>
        <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
          {ownerName && (
            <Box width="full" textAlign="left" gap="s8">
              <Text weight="medium" size="300" color="grey1000">
                {i18n.t('expenses_v2.transactions.detail.fields.employee')}
              </Text>
              <Text weight="normal" size="300" color="grey800">
                {ownerName}
              </Text>
            </Box>
          )}
          <Box width="full" textAlign="left" gap="s8">
            <Text weight="medium" size="300" color="grey1000">
              {i18n.t('expenses_v2.transactions.detail.fields.transaction_date')}
            </Text>
            <Text weight="normal" size="300" color="grey800">
              {dateBackendDateTimeToUI(date)}
            </Text>
          </Box>
        </Box>
        <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
          <Box width="full" textAlign="left" gap="s8">
            <Text weight="medium" size="300" color="grey1000">
              {i18n.t('expenses_v2.transactions.detail.fields.merchant')}
            </Text>
            <Text weight="normal" size="300" color="grey800">
              {merchantName}
            </Text>
          </Box>
          {exchangeRate === 1 && (
            <Box width="full" textAlign="left" gap="s8">
              <Text weight="medium" size="300" color="grey1000">
                {i18n.t('expenses_v2.transactions.detail.fields.amount')}
              </Text>
              <AmountWithCurrency amount={amountCents} currency={currency} />
            </Box>
          )}
        </Box>
        {exchangeRate !== 1 && (
          <>
            <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
              <Box width="full" textAlign="left" gap="s8">
                <Text weight="medium" size="300" color="grey1000">
                  {i18n.t('expenses_v2.transactions.detail.fields.amount')}
                </Text>
                <AmountWithCurrency amount={amountCents} currency={currency} />
              </Box>
              <Box width="full" textAlign="left" gap="s8">
                <Text weight="medium" size="300" color="grey1000">
                  {i18n.t('expenses_v2.transactions.detail.fields.original_amount')}
                </Text>
                <AmountWithCurrency amount={merchantAmountCents} currency={merchantCurrency} />
              </Box>
            </Box>
            <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
              <Box width="full" textAlign="left" gap="s8">
                <Text weight="medium" size="300" color="grey1000">
                  {i18n.t('expenses_v2.transactions.detail.fields.exchange_rate')}
                </Text>
                <Text weight="normal" size="300" color="grey800">
                  {exchangeRate}
                </Text>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default TransactionDetailContent
